import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
} from 'recharts';
import { BarChart3, Users, Eye, Clock, ArrowUpRight, TrendingUp, Loader } from 'lucide-react';

interface DailyData {
  date: string;
  sessions: number;
  pageViews: number;
  users: number;
}

interface TopPage {
  path: string;
  views: number;
}

interface Totals {
  sessions: number;
  pageViews: number;
  users: number;
  avgDuration: number;
  bounceRate: number;
}

interface AnalyticsData {
  daily: DailyData[];
  topPages: TopPage[];
  totals: Totals;
  period: string;
}

function formatDate(dateStr: string): string {
  if (dateStr.length !== 8) return dateStr;
  const month = dateStr.substring(4, 6);
  const day = dateStr.substring(6, 8);
  return `${day}/${month}`;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

const AnalyticsTab: React.FC = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState('30');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/.netlify/functions/analytics-data?period=${period}`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Erreur API');
      }
      const result = await res.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="w-8 h-8 text-red-500 animate-spin" />
        <span className="ml-3 text-gray-400">Chargement des analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-xl p-8 text-center">
        <BarChart3 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Analytics non disponibles</h3>
        <p className="text-gray-400 mb-4">{error}</p>
        <p className="text-gray-500 text-sm">
          Configurez GA_PROPERTY_ID et GA_SERVICE_ACCOUNT dans les variables Netlify.
        </p>
      </div>
    );
  }

  if (!data) return null;

  const chartData = data.daily.map((d) => ({
    ...d,
    date: formatDate(d.date),
  }));

  return (
    <div className="space-y-6">
      {/* Header + Period Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <TrendingUp className="w-6 h-6 text-red-500" />
          <h2 className="text-xl font-bold text-white">Google Analytics</h2>
        </div>
        <div className="flex space-x-2">
          {['7', '14', '30', '90'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                period === p
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {p}j
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard
          icon={Users}
          label="Visiteurs"
          value={data.totals.users.toLocaleString('fr-FR')}
        />
        <KPICard
          icon={Eye}
          label="Pages vues"
          value={data.totals.pageViews.toLocaleString('fr-FR')}
        />
        <KPICard
          icon={BarChart3}
          label="Sessions"
          value={data.totals.sessions.toLocaleString('fr-FR')}
        />
        <KPICard
          icon={Clock}
          label="Duree moy."
          value={formatDuration(data.totals.avgDuration)}
        />
        <KPICard
          icon={ArrowUpRight}
          label="Taux rebond"
          value={`${(data.totals.bounceRate * 100).toFixed(1)}%`}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sessions & Users */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
            Sessions et visiteurs
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="gradSessions" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#dc2626" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#dc2626" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f97316" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#6b7280" fontSize={11} />
              <YAxis stroke="#6b7280" fontSize={11} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: 13,
                }}
              />
              <Area
                type="monotone"
                dataKey="sessions"
                stroke="#dc2626"
                fill="url(#gradSessions)"
                strokeWidth={2}
                name="Sessions"
              />
              <Area
                type="monotone"
                dataKey="users"
                stroke="#f97316"
                fill="url(#gradUsers)"
                strokeWidth={2}
                name="Visiteurs"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Page Views */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
            Pages vues
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#6b7280" fontSize={11} />
              <YAxis stroke="#6b7280" fontSize={11} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: 13,
                }}
              />
              <Line
                type="monotone"
                dataKey="pageViews"
                stroke="#dc2626"
                strokeWidth={2}
                dot={{ fill: '#dc2626', r: 3 }}
                name="Pages vues"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Pages */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
          Pages les plus visitees
        </h3>
        <div className="space-y-3">
          {data.topPages.map((page, index) => {
            const maxViews = data.topPages[0]?.views || 1;
            const pct = (page.views / maxViews) * 100;
            return (
              <div key={page.path} className="flex items-center space-x-4">
                <span className="text-gray-500 text-sm w-6 text-right">{index + 1}.</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white text-sm font-medium truncate max-w-xs">
                      {page.path}
                    </span>
                    <span className="text-gray-400 text-sm ml-4">
                      {page.views.toLocaleString('fr-FR')}
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-red-600 to-red-500 rounded-full"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

function KPICard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.FC<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="bg-gray-800 rounded-xl p-4">
      <div className="flex items-center space-x-2 mb-2">
        <Icon className="w-4 h-4 text-red-500" />
        <span className="text-xs text-gray-400 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
    </div>
  );
}

export default AnalyticsTab;
